import { CalendarOutlined } from '@ant-design/icons';
import { FaSync } from 'react-icons/fa';
import { Card } from 'antd';
import dayjs from 'dayjs';

import { ExclamationTriangle, Trash3 } from 'react-bootstrap-icons';
import DefaultButton from '../../../../../components/buttons/DefaultButton';
import { PermissionEnum } from '../../../../../enums/PermissionEnum';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { useTranslation } from 'react-i18next';
import RESOURCE_CONFIGS from '../../../../../constants/resourceConfig';

import { useState } from 'react';
import TaskLocationModal from '../../../../../components/modal/TaskLocationModal';
import { TaskTypeEnum } from '../../../../../enums/TaskTypeEnum';
import Tag from '../../../../../components/tag/Tag';

interface ITask {
  item: any;
  getDeleteConfirmation: (id: number, pendingTasks: number, totalTasks: number) => void;
}

const RecurrentTask = (props: ITask) => {
  const { t } = useTranslation(['task']);
  const { item, getDeleteConfirmation } = props;

  const [isLocationModalOpen, setIsLocationModalOpen] = useState<boolean>(false);
  const [selectedInspection, setSelectedInspection] = useState<any>(null);

  const isOverDue = (date: any) => {
    return dayjs(date).isBefore(dayjs(), 'day');
  };

  const closeLocationModal = () => {
    setIsLocationModalOpen(false);
    setSelectedInspection(null);
  };

  const { hasPermission } = usePermissions();

  return (
    <>
      <Card className="@container m-0 w-full rounded-lg border border-gray-200 p-0">
        <div>
          <div className="md-items-center mb-2 flex w-full flex-col items-start justify-between gap-2 md:flex-row">
            <div className="flex flex-row flex-wrap gap-2">
              {/* Task Type Tag */}
              <Tag
                tagType={
                  item?.taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION ? 'brown' : 'info'
                }
                filled={true}
              >
                {t(`taskTypeEnum.${item?.taskTypeKey}`)}
              </Tag>

              {/* Legal Metrology Type Tag */}
              {item?.taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION && (
                <Tag tagType={'default'} filled={false}>
                  {t(`legalMetroTaskTypeEnum.${item?.subTaskTypeKey}`)}
                </Tag>
              )}
            </div>
          </div>
          <div className="mb-4 flex flex-row flex-wrap items-center gap-2 md:gap-4">
            <div>
              <h2 className="text-md mb-0 mr-3 font-semibold md:text-lg">{`${item?.name}`}</h2>
            </div>
          </div>
        </div>

        <div className="md:text-md my-2 flex w-full flex-col items-start justify-between space-y-2 text-sm md:space-y-4">
          {/* <div>
            <p className="flex items-center gap-1">
              <MdNumbers className="shrink-0" />
              <span>{item.totalTasks} tasks under this series</span>
            </p>
          </div> */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:flex-row sm:gap-16 md:gap-4">
            <>
              {isOverDue(Number(item?.endDate)) ? (
                <p className="w-fit rounded-md border border-red-500 px-2 text-red-500 md:px-4">
                  <ExclamationTriangle className="mb-1 mr-2 inline shrink-0" /> {t('due')} :{' '}
                  {dayjs(Number(item?.endDate)).format(RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY)}
                </p>
              ) : (
                <p className="flex items-center gap-1">
                  <CalendarOutlined className="shrink-0" /> {t('due')} :{' '}
                  {dayjs(Number(item?.endDate)).format(RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY)}
                </p>
              )}
            </>
          </div>
          <p className="flex items-center gap-1">
            <FaSync className="shrink-0" />
            <span>
              {item &&
                (() => {
                  if (!item.endDate || !item.interval || !item.frequency || !item.startDate) {
                    return null;
                  }

                  const frequencyMap: Record<string, string> = {
                    WEEKLY: 'week',
                    MONTHLY: 'month',
                    QUARTERLY: 'quarter',
                    YEARLY: 'year',
                    DAILY: 'day',
                  };

                  const formattedDate = dayjs(Number(item.endDate)).format(
                    RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY
                  );
                  const formattedStartDate = dayjs(Number(item.startDate)).format(
                    RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY
                  );

                  const unit = item.frequency;

                  const message =
                    item.interval === 1
                      ? `${t('taskScheduledEvery')} ${t(frequencyMap[unit])} ${t('from')} ${formattedStartDate} ${t('until')} ${formattedDate}.`
                      : `${t('taskScheduledOnceEvery')} ${item.interval} ${t(frequencyMap[unit])}s ${t('from')} ${formattedStartDate} ${t('until')} ${formattedDate}.`;

                  return <div className="text-sm text-gray-500">{message}</div>;
                })()}
            </span>
          </p>
        </div>

        <div className="mt-10 flex flex-col flex-wrap gap-3 text-sm md:flex-row md:items-center md:gap-6">
          {hasPermission([PermissionEnum.DELETE_INSPECTIONS]) && (
            <DefaultButton
              handleOnClick={() =>
                getDeleteConfirmation?.(item?.id, item.pendingTasksCount, item.totalTasks)
              }
            >
              <div className="flex flex-row items-center justify-center gap-2">
                <Trash3 className="h-4 w-4 text-black" />
                <p>{t('delete')}</p>
              </div>
            </DefaultButton>
          )}
        </div>
      </Card>

      <TaskLocationModal
        visible={isLocationModalOpen}
        onClose={closeLocationModal}
        inspection={selectedInspection}
      />
    </>
  );
};

export default RecurrentTask;
